import {ChangeDetectorRef, Component, inject} from '@angular/core';
import {FilterItemTree, isFilterTreeEmpty, NonemptyFilterItem, TableComponent} from '@icz/angular-table';
import {
  ButtonComponent,
  FormButtonsComponent,
  LabelComponent,
  LoadingIndicatorService,
  TooltipDirective
} from '@icz/angular-essentials';
import {
  CodebookService,
  DocumentSearchService,
  DocumentsTableColumn,
  DocumentsTableComponent,
  DocumentView,
  getUnitViewSelectorOptions,
} from '|shared';
import {injectModalData, injectModalRef} from '@icz/angular-modal';
import {TranslateService} from '@ngx-translate/core';
import {
  SharedFolderFilterDefinitionSearchDatasource
} from '|modules/shared-folders/modules/shared-folder-filter-definition-dialog/shared-folder-filter-definition-search.datasource';
import {DocumentDto, FileDto} from '|api/document';
import {ConfigPropValueService} from '|ui-essl-core';

export interface SharedFolderFilterDefinitionDialogData {
  searchTerm: Nullable<string>;
  activeFilters: Nullable<FilterItemTree>
}

export interface SharedFolderFilterDefinitionDialogResult {
  searchTerm: Nullable<string>;
  activeFilters: FilterItemTree;
}

@Component({
  selector: 'icz-shared-folder-filter-definition-dialog',
  templateUrl: './shared-folder-filter-definition-dialog.component.html',
  styleUrls: ['./shared-folder-filter-definition-dialog.component.scss'],
  imports: [
    ButtonComponent,
    DocumentsTableComponent,
    FormButtonsComponent,
    LabelComponent,
    TooltipDirective
  ]
})
export class SharedFolderFilterDefinitionDialogComponent {
  protected loadingService = inject(LoadingIndicatorService);
  protected documentSearchService = inject(DocumentSearchService);
  protected modalRef = injectModalRef<Nullable<SharedFolderFilterDefinitionDialogResult>>();
  protected configPropValueService = inject(ConfigPropValueService);
  protected changeDetectorRef = inject(ChangeDetectorRef);
  protected translateService = inject(TranslateService);
  protected codebookService = inject(CodebookService);
  protected modalData = injectModalData<SharedFolderFilterDefinitionDialogData>();

  protected selectorOptions$ = getUnitViewSelectorOptions(this.codebookService);

  currentSearchTerm = this.modalData.searchTerm;
  activeFilters = this.modalData.activeFilters;

  dataSource = new SharedFolderFilterDefinitionSearchDatasource(
    this.documentSearchService
  );

  get areNoCriteriaChosen() {
    return !this.currentSearchTerm && (!this.activeFilters || isFilterTreeEmpty(this.activeFilters));
  }

  submit() {
    this.modalRef.close({
      searchTerm: this.currentSearchTerm,
      activeFilters: this.activeFilters!,
    });
  }

  cancel() {
    this.modalRef.close();
  }

  searchTermChanged(searchTerm: string) {
    this.currentSearchTerm = searchTerm.trim();
  }

  activeFiltersChanged(activeFilters: FilterItemTree) {
    this.activeFilters = activeFilters;
  }

  docTableInitialized(table: TableComponent<DocumentDto | FileDto, number, DocumentsTableColumn>) {
    if (this.modalData.activeFilters) {
      if (table?.tableToolbarService) {
        this.modalData.activeFilters?.values.forEach(filterItem => {
          table.tableToolbarService.addFilterItem(filterItem as NonemptyFilterItem, true);
        });
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  protected readonly DocumentView = DocumentView;
}
